import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
//import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from '../../components/UI/LoadingSpinner';
import ScoutMindLogo from '../../components/Logo/ScoutMindLogo';
import { publicApi } from '../../services/apiClient';
import toast from 'react-hot-toast';

interface TokenValidation {
  valid: boolean;
  email?: string;
  expires_at?: string;
  message?: string;
}

const ResetPasswordPage: React.FC = () => {
  //const { t } = useTranslation();
  //const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValidation, setTokenValidation] = useState<TokenValidation>({ valid: false });
  const [isSuccess, setIsSuccess] = useState(false);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValidation({ 
          valid: false, 
          message: 'Token de restablecimiento no encontrado' 
        });
        setIsValidating(false);
        return;
      }

      try {
        const response = await publicApi.post('/auth/validate-reset-token', { token });
        setTokenValidation({
          valid: response.data.valido,
          email: response.data.email,
          expires_at: response.data.expires_at
        });
      } catch (error: any) {
        setTokenValidation({
          valid: false,
          message: error.response?.data?.mensaje || 'Token inválido o expirado'
        });
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const validatePasswords = () => {
    if (!newPassword.trim()) {
      toast.error('La nueva contraseña es requerida');
      return false;
    }

    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (newPassword.length > 128) {
      toast.error('La contraseña es demasiado larga');
      return false;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      await publicApi.post('/auth/reset-password', {
        token,
        new_password: newPassword
      });

      setIsSuccess(true);
      toast.success('Contraseña restablecida exitosamente');
      
    } catch (error: any) {
      toast.error(
        error.response?.data?.mensaje || 
        'Error al restablecer la contraseña. Inténtalo de nuevo.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
            <div className="flex justify-center mb-8">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>
            <LoadingSpinner size="lg" />
            <p className="text-white mt-4">Validando enlace de restablecimiento...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValidation.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
            <div className="flex justify-center mb-8">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>
            
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              Enlace Inválido
            </h1>
            <p className="text-blue-100 text-sm mb-6">
              {tokenValidation.message || 'El enlace de restablecimiento es inválido o ha expirado.'}
            </p>

            <div className="space-y-3">
              <Link
                to="/forgot-password"
                className="w-full py-3 px-4 text-white font-medium rounded-lg transition-colors text-center block"
                style={{ background: '#0F2A7D' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
              >
                Solicitar Nuevo Enlace
              </Link>
              
              <Link
                to="/login"
                className="w-full py-3 px-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors text-center block border border-white/20"
              >
                Volver al Inicio de Sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
            <div className="flex justify-center mb-8">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>

            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">
              ¡Contraseña Restablecida!
            </h1>
            <p className="text-blue-100 text-sm mb-6">
              Tu contraseña ha sido restablecida exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>

            <Link
              to="/login"
              className="w-full py-3 px-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-center block"
              style={{ background: '#0F2A7D' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#0A1F5C'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
            >
              Iniciar Sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <ScoutMindLogo className="h-12 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Nueva Contraseña
            </h1>
            <p className="text-blue-100 text-sm">
              {tokenValidation.email && (
                <>Restableciendo contraseña para <strong>{tokenValidation.email}</strong></>
              )}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Nueva Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder="Mínimo 6 caracteres"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Confirmar Contraseña *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-300" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                  placeholder="Repite la contraseña"
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password strength indicator */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2 text-xs">
                  <div className={`w-2 h-2 rounded-full ${newPassword.length >= 6 ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                  <span className={newPassword.length >= 6 ? 'text-green-400' : 'text-gray-400'}>
                    Al menos 6 caracteres
                  </span>
                </div>
                {confirmPassword && (
                  <div className="flex items-center space-x-2 text-xs">
                    <div className={`w-2 h-2 rounded-full ${newPassword === confirmPassword ? 'bg-green-400' : 'bg-red-400'}`}></div>
                    <span className={newPassword === confirmPassword ? 'text-green-400' : 'text-red-400'}>
                      Las contraseñas coinciden
                    </span>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !newPassword || !confirmPassword}
              className="w-full py-3 px-4 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ background: '#0F2A7D' }}
              onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = '#0A1F5C')}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0F2A7D'}
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>Restableciendo...</span>
                </div>
              ) : (
                'Restablecer Contraseña'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-blue-300 hover:text-blue-200 text-sm transition-colors"
            >
              Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;